import { Box } from '@mui/material';
import {
  EmailField,
  FormTab,
  Labeled,
  TextField,
  TextInput,
  required,
  useRecordContext,
  useDelete,
  useNotify,
  useRedirect,
  useResourceContext,
} from 'react-admin';
import gql from 'graphql-tag';

import CraftTabbedForm from 'src/components/CraftTabbedForm';
import { CraftPageSection } from 'src/components/CraftPageSection';
import CraftTwoColumns from 'src/components/CraftTwoColumns';
import { UserRoleInput } from 'src/inputs/UserRoleInput';
import { client } from 'src/data/api';

const DELETE_ADMIN_USER_RELATED_RECORDS = gql`
  mutation DeleteAdminUserRelatedRecords($userId: Int!) {
    delete_admin_access_token_invalidations(where: { user_id: { _eq: $userId } }) {
      affected_rows
    }
    delete_admin_refresh_tokens(where: { user_id: { _eq: $userId } }) {
      affected_rows
    }
  }
`;

const EditAdminUsersTabbedForm = () => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const notify = useNotify();
  const redirect = useRedirect();
  const [deleteOne] = useDelete();

  const dialogContent = `You are about to delete the user "${record?.email}". Are you sure?`;
  const dialogTitle = `Delete User #${record?.id}`;

  const handleDelete = async () => {
    if (!record) return;

    try {
      await client.mutate({
        mutation: DELETE_ADMIN_USER_RELATED_RECORDS,
        variables: { userId: record.id },
      });

      await deleteOne(resource, { id: record.id });

      notify('User deleted successfully', { type: 'success' });
      redirect('list', resource);
    } catch (error) {
      notify('Failed to delete user. Please try again.', { type: 'error' });
      console.error('Error deleting admin user:', error);
    }
  };

  return (
    <CraftTabbedForm
      formType="edit"
      deleteOptions={{
        deletePermission: 'user:delete',
        dialogContent,
        dialogTitle,
        onConfirmDelete: handleDelete,
      }}
    >
      <FormTab label="Details">
        <CraftPageSection title="User details">
          <CraftTwoColumns
            left={
              <Box display="flex" flexDirection="column" gap={2.5}>
                <Labeled label="ID">
                  <TextField source="id" />
                </Labeled>
                <Labeled label="Email">
                  <EmailField source="email" />
                </Labeled>
              </Box>
            }
            right={<TextInput fullWidth source="name" />}
          />
        </CraftPageSection>
        <CraftPageSection sx={{ mt: 3 }} title="Roles and Permissions">
          <UserRoleInput
            isRequired // excludes "empty" option
            validate={required()}
          />
        </CraftPageSection>
      </FormTab>
    </CraftTabbedForm>
  );
};

export default EditAdminUsersTabbedForm;
